/**
 * App Catalogue — Entitlement Preview page.
 *
 * Standalone page wrapping the existing `EntitlementPreview` component: pick an
 * app from the catalogue, then enter sample roles + rights to see whether that
 * user would be entitled to the app (with a per-clause breakdown).
 *
 * The evaluation itself is reused from `entitlement.ts` (via the
 * `EntitlementPreview` component), which mirrors the backend evaluator exactly.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconArrowLeft, IconShieldCheck } from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Title,
  Text,
  Badge,
  Select,
  Button,
  Alert,
  Loader,
  Center,
  Divider,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { useApplicationCatalogueService } from '@actschurch/ccms-sdk';
import type { AppManifest } from '@actschurch/ccms-sdk';
import { ADMIN_ROUTES } from '@/config/routes';
import { EntitlementPreview } from './EntitlementPreview';
import { isEntitlementEmpty } from './entitlement';

const CATALOGUE_QUERY_KEY = ['admin', 'app-catalogue', 'all'];

export default function EntitlementPreviewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const catalogueService = useApplicationCatalogueService();
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);

  const {
    data: apps = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: CATALOGUE_QUERY_KEY,
    queryFn: () => catalogueService.getAllApps(),
  });

  const selectData = useMemo(
    () =>
      (apps as AppManifest[]).map((a) => ({
        value: a.appId,
        label: `${a.displayName} (${a.appId})`,
      })),
    [apps]
  );

  const selectedApp = useMemo(
    () => (apps as AppManifest[]).find((a) => a.appId === selectedAppId) ?? null,
    [apps, selectedAppId]
  );

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <div>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate(ADMIN_ROUTES.APP_CATALOGUE_OVERVIEW)}
          >
            Back to overview
          </Button>
          <Title order={2} mt="xs">
            Entitlement Preview
          </Title>
          <Text c="dimmed" size="sm">
            Pick an app, then enter a sample user's roles and rights to check whether they would see
            it. Mirrors the backend entitlement evaluator exactly.
          </Text>
        </div>

        {error ? (
          <Alert color="red" title="Service Unavailable">
            <Text size="sm">
              {error instanceof Error ? error.message : 'Failed to load the app catalogue.'}
            </Text>
            <Button
              variant="light"
              color="red"
              size="xs"
              mt="sm"
              onClick={() => queryClient.invalidateQueries({ queryKey: CATALOGUE_QUERY_KEY })}
            >
              Retry
            </Button>
          </Alert>
        ) : isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : (
          <Card withBorder padding="lg" radius="md">
            <Stack gap="md">
              <Group gap="xs">
                <IconShieldCheck size={18} />
                <Title order={4}>Choose an app</Title>
              </Group>
              <Select
                placeholder="Select an app to preview"
                data={selectData}
                value={selectedAppId}
                onChange={setSelectedAppId}
                searchable
                clearable
                nothingFoundMessage="No apps in the catalogue"
              />

              {selectedApp ? (
                <>
                  <Divider
                    label={`Entitlement for ${selectedApp.displayName}`}
                    labelPosition="left"
                  />
                  {isEntitlementEmpty(selectedApp.entitlement) ? (
                    <Alert color="blue">
                      This app has an empty entitlement predicate — every authenticated user can see
                      it.
                    </Alert>
                  ) : (
                    <Group gap="xs" wrap="wrap">
                      {(selectedApp.entitlement.anyOfRoles ?? []).map((r) => (
                        <Badge key={`role-${r}`} variant="light" color="blue">
                          role: {r}
                        </Badge>
                      ))}
                      {(selectedApp.entitlement.anyOfRights ?? []).map((r) => (
                        <Badge key={`anyright-${r}`} variant="light" color="teal">
                          any: {r}
                        </Badge>
                      ))}
                      {(selectedApp.entitlement.allOfRights ?? []).map((r) => (
                        <Badge key={`allright-${r}`} variant="light" color="grape">
                          all: {r}
                        </Badge>
                      ))}
                    </Group>
                  )}
                  <EntitlementPreview entitlement={selectedApp.entitlement} />
                </>
              ) : (
                <Text size="sm" c="dimmed">
                  Select an app above to preview its entitlement.
                </Text>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
    </FadeIn>
  );
}
